import { useEffect, useRef, useState } from 'react';

const TICK_MS = 100;

/**
 * Live elapsed-time counter for long AI calls. Keeps the final duration visible after the run ends
 * so the modal can show "took 42.3s" without the caller tracking timestamps.
 */
export default function useElapsedTimer(isRunning: boolean): number {
  const [elapsedMs, setElapsedMs] = useState(0);
  const startedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isRunning) {
      startedAtRef.current = null;
      return;
    }

    startedAtRef.current = Date.now();
    setElapsedMs(0);

    const interval = setInterval(() => {
      if (startedAtRef.current !== null) {
        setElapsedMs(Date.now() - startedAtRef.current);
      }
    }, TICK_MS);

    return () => clearInterval(interval);
  }, [isRunning]);

  return elapsedMs;
}

export function formatElapsed(ms: number): string {
  const totalSeconds = ms / 1000;

  if (totalSeconds < 60) {
    return `${totalSeconds.toFixed(1)}s`;
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
}
